import type { User } from '../types';

type ActingUser = Pick<User, 'id' | 'role'> & Partial<Pick<User, 'actingFor'>>;

/**
 * The user whose book this session works. For an assistant this is the sales
 * rep she acts for; for everyone else it is the signed-in user. Use it for
 * "is this record mine" checks. Authorship checks ("did I write this") keep
 * using the signed-in user's own id.
 */
export function getBookUserId(user: ActingUser | null | undefined): string | null {
  if (!user) return null;
  return user.actingFor?.id ?? user.id;
}

export function isAssistantUser(user: Pick<User, 'role'> | null | undefined): boolean {
  return user?.role === 'ASSISTANT';
}

/** First whitespace-delimited token of the principal's name, or null when not acting for anyone. */
export function actingForFirstName(user: Partial<Pick<User, 'actingFor'>> | null | undefined): string | null {
  const name = user?.actingFor?.displayName?.trim();
  if (!name) return null;
  return name.split(/\s+/)[0] ?? null;
}

/**
 * Swap a leading "My"/"Mine" label for the principal's possessive when the
 * session is acting for someone, e.g. "My Tasks" -> "Josh's Tasks".
 * Without a usable first name the label is returned unchanged.
 */
export function bookLabel(user: Partial<Pick<User, 'actingFor'>> | null | undefined, label: string): string {
  const first = actingForFirstName(user);
  if (!first) return label;
  return label.replace(/^(My|Mine)\b/, `${first}'s`);
}
